import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateLabel } from '@/services/workflow/update-label';
import { ORG_LABELS_QUERY_KEY } from '@/hooks/queries/use-get-org-labels';
import { useOrgStore } from '@/stores/org-store';
import { Label } from '@/types/workflow-types';
import * as Sentry from '@sentry/react';

interface UpdateOrgLabelInput {
  id: string;
  name?: string;
  description?: string | null;
  color?: string | null;
}

export function useUpdateOrgLabel() {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();

  return useMutation<Label, Error, UpdateOrgLabelInput>({
    mutationFn: async ({ id, ...data }) => {
      if (!orgId) throw new Error('No org ID');
      const response = await updateLabel(orgId, id, data);
      if (response.detail || !response.data) {
        throw new Error(response.detail || 'Failed to update label');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORG_LABELS_QUERY_KEY] });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: { context: 'update-org-label' },
      });
    },
  });
}
